import { Component, OnInit, ViewChild, ElementRef, NgZone, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Category, TechnicalServiceFilter, TechnicalService, SearchResult, SubCategory } from '../../interfaces';
import { MapsAPILoader } from '@agm/core';
import { MapService, GeolocationService, CategoryService, TechnicalServiceService } from 'src/app/services';
import { SearchPanelComponent } from './search-panel.component';
import { PlaceService } from 'src/app/services/place.service';

/**
 * Variable to use the Google API
 */
declare const google: any;
/**
 * Search panel to filter the technical services
 *
 * @export
 * @class SearchCategoryPanelComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-search-category-panel',
  templateUrl: './search-category-panel.component.html',
  styleUrls: ['./search-panel.component.scss'],
})
export class SearchCategoryPanelComponent extends SearchPanelComponent implements OnInit {
  /**
   * Search input reference
   *
   * @type {ElementRef}
   * @memberof SearchCategoryPanelComponent
   */
  @ViewChild('addressSearch', {
    read: false,
    static: true,
  })
  addressSearchRef: ElementRef;

  /**
   * Emit an event with all the results
   *
   * @type {EventEmitter<SearchResult[]>}
   * @memberof SearchCategoryPanelComponent
   */
  @Output() searchResult: EventEmitter<SearchResult> = new EventEmitter();

  /**
   * Search Form
   *
   * @type {FormGroup}
   * @memberof SearchCategoryPanelComponent
   */
  searchForm: FormGroup;

  /**
   * Main categories
   *
   * @type {Category[]}
   * @memberof SearchCategoryPanelComponent
   */
  categories: Category[] = [];

  /**
   * Secondary categories (optional)
   *
   * @type {SubCategory[]}
   * @memberof SearchCategoryPanelComponent
   */
  subCategories: SubCategory[] = [];

  /**
   * Flag to show a loading indicator when the user search for locations
   *
   * @type {boolean}
   * @memberof SearchCategoryPanelComponent
   */
  isSearching: boolean = false;

  /**
   * Flag to show a loading indicator when the browser gets the current geolocation
   *
   * @type {boolean}
   * @memberof SearchCategoryPanelComponent
   */
  isGeolocating: boolean = false;

  /**
   * Flag to show inputs of categories to search
   *
   * @type {boolean}
   * @memberof SearchCategoryPanelComponent
   */
  isSearchCategories: boolean = true;

  /**
   * Creates an instance of SearchPanelComponent.
   * @param {FormBuilder} fb
   * @param {CategoryService} categoryService
   * @param {NgZone} ngZone
   * @param {MapsAPILoader} mapApiLoader
   * @param {MapService} mapService
   * @param {GeolocationService} geolocationService
   * @param {TechnicalServiceService} technicalServiceService
   * @memberof SearchCategoryPanelComponent
   */
  constructor(
    protected fb: FormBuilder,
    private categoryService: CategoryService,
    protected ngZone: NgZone,
    protected mapApiLoader: MapsAPILoader,
    protected mapService: MapService,
    protected geolocationService: GeolocationService,
    protected technicalServiceService: TechnicalServiceService,
    protected placeService: PlaceService
  ) {
    super(fb, ngZone, mapApiLoader, mapService, geolocationService, technicalServiceService, placeService);
  }

  /**
   * - Bind Google places input
   * - Load main categories
   *
   * @memberof SearchCategoryPanelComponent
   */
  ngOnInit() {
    this.createForm();
    this.initAutocomplete();
    this.loadCategories();
  }

  setSearchFilter(form: FormGroup): TechnicalServiceFilter {
    const searchFilter: TechnicalServiceFilter = {
      category: form.value.category,
      subCategory: form.value.subCategory,
      coords: form.value.coords,
      postalCode: ''
    };
    this.notifyDirectChange(searchFilter);
    return searchFilter;
  }

  /**
   * If the category selected includes direct change it's show on the screen
   *
   * @private
   * @param {TechnicalServiceFilter} searchFilter
   * @memberof SearchCategoryPanelComponent
   */
  private notifyDirectChange(searchFilter: TechnicalServiceFilter) {
    const category = searchFilter.subCategory || searchFilter.category;

    if (!!category.attentionModel) {
      this.categoryService.notifyDirectChange(category.attentionModel);
    } else {
      this.categoryService.notifyDirectChange(null);
    }
  }

  /**
   * Load secondary categories based on selected main category and set select validation
   *
   * @memberof SearchCategoryPanelComponent
   */
  async loadSubcategories() {
    // Clean options
    this.subCategories = [];

    // Remove validation
    this.searchForm.removeControl('subCategory');

    const categoryControl: FormControl = this.searchForm.get('category') as FormControl;

    // If the user selected the main category
    if (categoryControl.value) {
      this.subCategories = await this.categoryService.getSubCategories(categoryControl.value);

      // If there are subcategories then add dynamic validation
      if (this.subCategories && this.subCategories.length) {
        this.searchForm.addControl('subCategory', new FormControl(null, [Validators.required]));
      }
    }
  }

  /**
   *
   * @param {*} _
   * @param {Category} category
   * @returns
   * @memberof SearchCategoryPanelComponent
   */
  trackById(_: any, category: Category) {
    return category.id;
  }

  /**
   * Load main categories
   *
   * @private
   * @memberof SearchCategoryPanelComponent
   */
  private async loadCategories() {
    this.categories = await this.categoryService.getCategories();
  }

  /**
   * Init form with specific validations
   *
   * @private
   * @memberof SearchCategoryPanelComponent
   */
  protected createForm() {
    this.searchForm = this.fb.group({
      category: [null, [Validators.required]],
      location: [''],
      coords: ['', [Validators.required]],
    });

    // If the user change the location the coords are nulled
    this.searchForm.get('location').valueChanges.subscribe((val) => !val && this.setCoordsOnSearchForm(null));
  }

  protected async searchTechnicalServicesAndLocateInMap(searchFilter: TechnicalServiceFilter): Promise<any> {
    const technicalServices: TechnicalService[] = await this.technicalServiceService.search(searchFilter);

    this.updateData(technicalServices, searchFilter.coords);
    this.emitEvent(technicalServices, searchFilter);
  }
}
